import { ref } from 'vue'
import MediaService from '../services/media.service'

interface UploadFile {
  file: File
  filename: string
  folder: string
  type: string
  size: number
  locationId: string
  groupId: string
  progressUpdate?: (progress: number) => void
}

interface UploadResponse {
  id: string
  url: string
  name: string
  type: string
  size: number
  status: string
}

export function useUpload() {
  const uploading = ref(false)
  const uploadProgress = ref(0)
  const uploadResponse = ref<UploadResponse | null>(null)
  const videoUploaded = ref(false)
  const fileExceedsMaxSize = ref(false)
  // Monotonically incrementing IDs to identify individual uploads. currentUploadId
  // advances synchronously at the start of each uploadFile call; uploadResponseId is
  // set to that value when the matching response resolves. Callers can compare the two
  // to discard responses that belong to a superseded upload.
  const currentUploadId = ref(0)
  const uploadResponseId = ref(0)

  // File size limits
  const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024 // 2GB
  const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

  const checkIfFileExceedsMaxSize = (file: File): boolean => {
    if (file.type.includes('video')) {
      return file.size > MAX_VIDEO_SIZE
    }
    return file.size > MAX_FILE_SIZE
  }

  const loadFiles = (file: File, uploadSource: string, locationId: string, groupId?: string): ReturnType<typeof uploadFile> => {
    let folder = 'others'

    // Determine folder based on file type
    if (file.type.includes('video')) {
      folder = uploadSource || 'videos'
    } else if (file.type.includes('image')) {
      folder = uploadSource || 'images'
    } else if (file.type.includes('audio')) {
      folder = uploadSource || 'others'
    }

    // Check file size
    fileExceedsMaxSize.value = checkIfFileExceedsMaxSize(file)

    if (fileExceedsMaxSize.value) {
      const limit = file.type.includes('video') ? '2GB' : '50MB'
      throw new Error(`File size exceeds max upload limit of ${limit}`)
    }

    // Start upload
    return uploadFile(file, file.name, folder, locationId, groupId || '')
  }

  const uploadFile = async (file: File, filename: string, folder: string, locationId: string, groupId: string) => {
    // Stamp this upload synchronously so callers can read currentUploadId right after
    // loadFiles() returns and compare it against uploadResponseId in their watcher.
    const myUploadId = ++currentUploadId.value
    try {
      uploading.value = true
      uploadProgress.value = 0

      const payload: UploadFile = {
        file,
        filename,
        folder,
        type: file.type,
        size: file.size,
        locationId,
        groupId,
        progressUpdate: (progress: number) => {
          uploadProgress.value = progress
        },
      }

      const result = await MediaService.uploadFile(payload)
      uploadResponse.value = await result.uploadPromise
      uploadResponseId.value = myUploadId
      videoUploaded.value = true
    } catch (error) {
      // Clear stale response so watchers do not fire a false-success notification
      // if a previous upload's uploadResponse.value is still set.
      uploadResponse.value = null
      console.error('Upload error:', error)
      throw error
    } finally {
      uploading.value = false
    }
  }

  return {
    uploading,
    uploadProgress,
    uploadResponse,
    uploadResponseId,
    currentUploadId,
    videoUploaded,
    loadFiles,
    fileExceedsMaxSize,
  }
}
